import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import axios from "axios"
import { getDeviceName } from "react-native-device-info"
import DeviceInfo from 'react-native-device-info';

import HttpRoutingService from "../../shared/services/httpRoutingService"

import { ZenToast } from "zen-core-elements"
import { API } from "../../shared/apiRoutes";
import { getClientName } from "../../platform/root/clientName";


interface initialState {
    registerUser: { value: string | null, loading: boolean, error: string | null }
    forgotPin: { value: string | null, loading: boolean }
    signupResponse: { value: null | signupResponse, error: string | null, }
    checkPasswordValid: { value: null | checkPasswordValidResponse, loading: boolean }
}
type updatePinResponse = { "code": string, "message": string, "pinUpdated": { "message": string, "result": {}, "success": boolean }, "success": boolean }


export interface checkPasswordValidResponse {
    "code": string,
    "message": string,
    "passwordExists": boolean,
    "success": boolean
}

export interface signInResponse {
    "code": string,
    "message": string,
    "accessToken": string,
    "user": {
        "address": string,
        "countryIsoCode": string,
        "userId": string,
        "firstName": string,
        "phone": string,
        "countryCode": number,
        "posRoleId": number,
        "store": {
            "id": number,
            "name": string,
            "isActive": boolean,
            "displayName": string,
            "currency": {
                "id": number,
                "currencyCode": string,
                "currencySymbol": string
            }
        },
        "name": string,
        "storeId": number,
        "profileImage": null,
        "infoId": number
    },
    "success": boolean
}
export type verifyTokenApiResponse =
    { "code": string, "message": string, "success": boolean, "user": { "clientId": string, "countryCode": number, "countryIsoCode": string, "email": string, "expiresAt": number, "firstName": string, "isActive": boolean, "phone": string, "roleCode": string, "userId": string, "validToken": string } }
export type signupPayload = {
    "store": {
        "name": string,
        "industryId": number
    },
    "storeDetails": {
        "currencyId": number,
        "countryCode": number,
        "countryIsoCode": string,
        "referalCode": string | null
    },
    "adminData": {
        "address": {
            "addressLine1": string,
            "city": string,
            "state": string,
            "country": string,
            "zipCode": string
        },
        "countryCode": number,
        "countryIsoCode": string,
        "firstName": string,
        "phone": string,
        "roleCode": string,
        "isActive": boolean,
        "currencyCode": string
    }
}
export type signupResponse = { "code": string, "message": string, "storeId": string, "success": true }
const initialState: initialState = {
    registerUser: { loading: false, value: null, error: null },
    forgotPin: { loading: false, value: null },
    signupResponse: { value: null, error: null },
    checkPasswordValid: { value: null, loading: false },
}

export const registerUser = createAsyncThunk("auth/registerUser", (email: { email: string }) => {
    return HttpRoutingService?.postMethod<string>("auth/register/email", email, null, "v2/")
})
export const forgotPinApi = createAsyncThunk("auth/forgotPinApi", (email: { email: string }) => {
    return HttpRoutingService?.postMethod<string>("auth/forgotpin", email, null, "v2/")
})
export const appSignIn = createAsyncThunk("auth/appSignIn", async (base64String: string, { rejectWithValue }) => {
    try {
        let deviceName = await getDeviceName()
        const ipAddress = await DeviceInfo.getIpAddress().then(ip => {
            return ip
        });
        let data = {
            deviceName: deviceName, browser: getClientName(), // "Mobile App" on Android; the real browser on web
            ipAddress: ipAddress,
        }
        console.log(base64String, "base64String");

        const response = await HttpRoutingService?.postMethod<signInResponse>("auth/signin", data, null, "v2/", { registration: base64String })
        // const response = await HttpRoutingService?.postMethod<signInResponse>("admin/signin", data, null, "v1/", { registration: base64String })
        return response
    }
    catch (error) {
        if (axios.isAxiosError(error)) {
            return rejectWithValue(error.response);
        }
    }
})
export const verifyTokenApi = createAsyncThunk("auth/verifyTokenApi", async ({ token }: { token: string }) => {
    return HttpRoutingService?.postMethod<verifyTokenApiResponse>("auth/verify/token", { token }, null, "v2/")
})
export const updatePinApi = createAsyncThunk("auth/updatePinApi", async ({ registration, pin }: { registration: string, pin: string }, { rejectWithValue }) => {
    try {
        const response = await HttpRoutingService?.putMethod<updatePinResponse>("auth/update/pin", { status: true }, null, "v2/", { registration, pin })
        return response
    }
    catch (error) {
        if (axios.isAxiosError(error)) {
            return rejectWithValue(error.response);
        }
    }
})

export const changePinApi = createAsyncThunk("auth/changePinApi", async ({ token, base64String, clientId }: { token: string, base64String: string, clientId: string }, { rejectWithValue }) => {
    try {
        const response = await HttpRoutingService?.postMethod<{ success: boolean }>("auth/change/pin", { token, clientId }, null, "v2/", { registration: base64String })
        return response
    }
    catch (error) {
        if (axios.isAxiosError(error)) {
            return rejectWithValue(error.response);
        }
    }
})
export const checkPasswordForAdminProfile = createAsyncThunk("auth/checkPasswordForAdminProfile", async ({ registration }: { registration: string }, { rejectWithValue }) => {
    try {
        // registration carries a PIN credential (email:pin:XXXX); tell the backend to verify it in PIN mode
        const response = await HttpRoutingService?.getMethod<checkPasswordValidResponse>(API.checkPasswordForAdminProfile, {}, "v1/", true, { registration, authentication: "pin" })
        return response
    }
    catch (error) {
        if (axios.isAxiosError(error)) {
            return rejectWithValue(error.response);
        }
    }
})

export const appSignup = createAsyncThunk("auth/appSignup", async ({ base64String, payload }: { base64String: string, payload: signupPayload }) => {
    return await HttpRoutingService?.postMethod<signupResponse>("auth/signup", payload, null, "v2/", { registration: base64String })
})
export const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(registerUser?.pending, (state) => {
                state.registerUser.value = ""
                state.registerUser.loading = true
            })
            .addCase(registerUser?.fulfilled, (state, action) => {
                state.registerUser.loading = false
                state.registerUser.value = action?.payload
            })
            .addCase(registerUser?.rejected, (state, action) => {
                state.registerUser.loading = false
                ZenToast({
                    type: "error",
                    message: "Your account could not be verified. Kindly contact admin to verify your account."
                })
            })
            .addCase(checkPasswordForAdminProfile?.pending, (state) => {
                state.checkPasswordValid.loading = true
            })
            .addCase(checkPasswordForAdminProfile?.fulfilled, (state, action) => {
                state.checkPasswordValid.loading = false
                state.checkPasswordValid.value = action?.payload ?? null
            })
            .addCase(checkPasswordForAdminProfile?.rejected, (state, action) => {
                state.checkPasswordValid.loading = false
                ZenToast({
                    type: "error",
                    message: "Something went wrong in checking the password match."
                })
            })
            .addCase(forgotPinApi?.pending, (state) => {
                state.forgotPin.value = ""
                state.forgotPin.loading = true
            })
            .addCase(forgotPinApi?.fulfilled, (state, action) => {
                state.forgotPin.loading = false
                state.forgotPin.value = action?.payload
            })
            .addCase(forgotPinApi?.rejected, (state, action) => {
                state.forgotPin.loading = false
                ZenToast({
                    type: "error",
                    message: "Failed to send a mail, please try again after some time!"
                })
            })
            .addCase(appSignup?.fulfilled, (state, action) => {
                state.signupResponse.value = action?.payload
                state.signupResponse.error = null
            })
            .addCase(appSignup?.rejected, (state, action) => {
                state.signupResponse.value = null
                state.signupResponse.error = action?.error?.message ?? "Signup error"
            })

    }
})

export default authSlice?.reducer